/**
 * ProcurementDrillDrawer — generic right-side detail drawer for the
 * Procurement Dashboard tiles. Each tile gets its own column set
 * + actions; the shell (Sheet wrapper + loading state + close) is
 * shared so all tiles feel identical.
 *
 * Tile 4 ("Missing vendor price") gets the inline "+ Add vendor &
 * price" action via <AddVendorPriceDialog>.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Plus, ExternalLink, Download } from "lucide-react";
import { AddVendorPriceDialog } from "./AddVendorPriceDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILE_META = {
  rms_without_bom: {
    title: "RMs without any BOM",
    endpoint: "/procurement-dashboard/rms-without-bom",
    columns: [
      { key: "rm_id", label: "RM ID" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "status", label: "Status" },
    ],
  },
  new_rms: {
    title: "New RMs in period",
    endpoint: "/procurement-dashboard/rms-new",
    columns: [
      { key: "rm_id", label: "RM ID" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "created_by", label: "Created by" },
      { key: "created_at", label: "Created at", fmt: (v) => v ? new Date(v).toLocaleString() : "" },
    ],
  },
  price_increases: {
    title: "Price increases in period",
    endpoint: "/procurement-dashboard/price-increases",
    columns: [
      { key: "rm_id", label: "RM ID" },
      { key: "rm_name", label: "Name" },
      { key: "vendor_name", label: "Vendor" },
      { key: "old_price", label: "Old ₹", fmt: (v) => v?.toFixed?.(2) ?? v },
      { key: "new_price", label: "New ₹", fmt: (v) => v?.toFixed?.(2) ?? v },
      { key: "pct_change", label: "% ↑", fmt: (v) => `+${v}%` },
      { key: "date", label: "Date", fmt: (v) => v ? new Date(v).toLocaleDateString() : "" },
    ],
  },
  missing_vendor_price: {
    title: "RMs missing a vendor price",
    endpoint: "/procurement-dashboard/missing-vendor-price",
    columns: [
      { key: "rm_id", label: "RM ID" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
    ],
  },
  shortages_without_po: {
    title: "Shortages with no open PO",
    endpoint: "/procurement-dashboard/shortages-without-po",
    columns: [
      { key: "rm_id", label: "RM ID" },
      { key: "branch", label: "Branch" },
      { key: "shortage", label: "Deficit", fmt: (v) => v },
      { key: "suggested_vendor", label: "Suggested vendor" },
      { key: "lowest_price", label: "Lowest ₹", fmt: (v) => v == null ? "—" : v.toFixed(2) },
    ],
  },
  overdue_po_receipts: {
    title: "Overdue PO receipts",
    endpoint: "/procurement-dashboard/overdue-po-receipts",
    columns: [
      { key: "po_number", label: "PO #" },
      { key: "vendor_name", label: "Vendor" },
      { key: "total_amount", label: "Value", fmt: (v) => `₹${v?.toLocaleString?.("en-IN") ?? v}` },
      { key: "expected_delivery_date", label: "Expected", fmt: (v) => v ? new Date(v).toLocaleDateString() : "" },
      { key: "days_late", label: "Days late" },
      { key: "status", label: "Status" },
    ],
  },
  spend: {
    title: "POs raised in period",
    endpoint: "/procurement-dashboard/spend",
    columns: [
      { key: "po_number", label: "PO #" },
      { key: "vendor_name", label: "Vendor" },
      { key: "total_amount", label: "Value", fmt: (v) => `₹${v?.toLocaleString?.("en-IN") ?? v}` },
      { key: "status", label: "Status" },
      { key: "order_date", label: "Ordered", fmt: (v) => v ? new Date(v).toLocaleDateString() : "" },
    ],
  },
  stale_vendor_prices: {
    title: "Vendor prices > 6 months old",
    endpoint: "/procurement-dashboard/stale-vendor-prices",
    columns: [
      { key: "vendor_name", label: "Vendor" },
      { key: "rm_id", label: "RM ID" },
      { key: "rm_name", label: "Name" },
      { key: "price", label: "₹", fmt: (v) => v?.toFixed?.(2) ?? v },
      { key: "days_since_updated", label: "Days old" },
    ],
  },
};

function csvDownload(rows, columns, filename) {
  const header = columns.map((c) => c.label).join(",");
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "");
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    }).join(",")
  ).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function ProcurementDrillDrawer({ tileKey, period, open, onClose, onChanged }) {
  const meta = TILE_META[tileKey];
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addPriceFor, setAddPriceFor] = useState(null); // {rm_id, name} when tile 4 row clicked

  useEffect(() => {
    if (!meta) return;
    let cancelled = false;
    // Defer the synchronous setLoading(true) into a microtask so the
    // react-hooks/set-state-in-effect rule sees only post-await writes
    // (functional pattern; no semantic change).
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const periodAware = ["new_rms", "price_increases", "spend"].includes(tileKey);
        const url = periodAware ? `${API}${meta.endpoint}?period=${period}` : `${API}${meta.endpoint}`;
        const res = await axios.get(url);
        if (cancelled) return;
        setItems(res.data?.items || []);
        setTotal(res.data?.total ?? (res.data?.items?.length ?? 0));
      } catch {
        if (!cancelled) toast.error("Failed to load detail rows");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [tileKey, period, meta]);

  if (!meta) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid={`drill-${tileKey}`}>
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="font-[Chivo]">{meta.title}</SheetTitle>
                <SheetDescription className="font-mono text-xs">
                  {loading ? "loading…" : `${total} row${total === 1 ? "" : "s"}`}
                </SheetDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => csvDownload(items, meta.columns, `${tileKey}-${Date.now()}.csv`)}
                  disabled={loading || items.length === 0}
                  data-testid={`drill-${tileKey}-csv`}
                >
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
              </div>
            </div>
          </SheetHeader>

          {/* Table */}
          <div className="mt-4 border border-border rounded-sm overflow-hidden">
            <table className="w-full text-xs font-mono">
              <thead className="bg-zinc-50">
                <tr>
                  {meta.columns.map((c) => (
                    <th key={c.key} className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-muted-foreground border-b">
                      {c.label}
                    </th>
                  ))}
                  {tileKey === "missing_vendor_price" && (
                    <th className="text-right px-3 py-2 border-b" />
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={meta.columns.length + 2} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={meta.columns.length + 2} className="text-center py-8 text-muted-foreground">No rows</td></tr>
                ) : items.map((r, idx) => (
                  <tr key={idx} className="border-b hover:bg-zinc-50" data-testid={`drill-row-${tileKey}-${idx}`}>
                    {meta.columns.map((c) => (
                      <td key={c.key} className="px-3 py-2 text-zinc-800">
                        {c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "—")}
                      </td>
                    ))}
                    {tileKey === "missing_vendor_price" && (
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAddPriceFor({ rm_id: r.rm_id, name: r.name })}
                          data-testid={`drill-add-price-${r.rm_id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add price
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SheetContent>
      </Sheet>

      {addPriceFor && (
        <AddVendorPriceDialog
          rm={addPriceFor}
          open={!!addPriceFor}
          onClose={() => setAddPriceFor(null)}
          onSuccess={() => {
            setAddPriceFor(null);
            // Re-fetch the drawer rows + summary tile count
            setItems((prev) => prev.filter((r) => r.rm_id !== addPriceFor.rm_id));
            setTotal((t) => Math.max(0, t - 1));
            onChanged?.();
            toast.success(`Price added for ${addPriceFor.rm_id}`);
          }}
        />
      )}
    </>
  );
}
